import { useState, useRef, useEffect } from "react";

const MONTHS = ["January","February","March","April","May","June","July","August","September","October","November","December"];
const DAYS_SHORT = ["Su","Mo","Tu","We","Th","Fr","Sa"];

function toStr(date) {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, "0");
  const d = String(date.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
}

function getCalendarDays(year, month) {
  const first = new Date(year, month, 1);
  const days = [];
  for (let i = 0; i < first.getDay(); i++) {
    days.push({ date: new Date(year, month, i - first.getDay() + 1), current: false });
  }
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  for (let d = 1; d <= daysInMonth; d++) {
    days.push({ date: new Date(year, month, d), current: true });
  }
  let next = 1;
  while (days.length < 35) {
    days.push({ date: new Date(year, month + 1, next++), current: false });
  }
  return days;
}

export default function DatePicker({ value, onChange }) {
  const [open, setOpen] = useState(false);
  const [viewMonth, setViewMonth] = useState(() => {
    if (value) {
      const [y, m] = value.split("-").map(Number);
      return new Date(y, m - 1, 1);
    }
    return new Date();
  });
  const ref = useRef(null);
  const todayStr = toStr(new Date());

  useEffect(() => {
    function handle(e) {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    }
    if (open) document.addEventListener("mousedown", handle);
    return () => document.removeEventListener("mousedown", handle);
  }, [open]);

  const displayDate = value
    ? new Date(value + "T12:00:00").toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })
    : "Select date";

  const days = getCalendarDays(viewMonth.getFullYear(), viewMonth.getMonth());

  function select(dateStr) {
    onChange(dateStr);
    setOpen(false);
  }

  function prevMonth() {
    setViewMonth(d => new Date(d.getFullYear(), d.getMonth() - 1, 1));
  }
  function nextMonth() {
    setViewMonth(d => new Date(d.getFullYear(), d.getMonth() + 1, 1));
  }

  const triggerStyle = {
    width: "100%",
    background: "#111113",
    border: "1px solid #2a2a2e",
    borderRadius: 7,
    padding: "9px 11px",
    color: value ? "#e8e6e0" : "#444",
    fontSize: 13,
    fontFamily: "'DM Mono', monospace",
    boxSizing: "border-box",
    cursor: "pointer",
    textAlign: "left",
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
  };

  const popupStyle = {
    position: "absolute",
    top: "calc(100% + 4px)",
    left: 0,
    zIndex: 400,
    background: "#1a1a1d",
    border: "1px solid #2a2a2e",
    borderRadius: 10,
    padding: "14px",
    width: 240,
    boxShadow: "0 8px 32px rgba(0,0,0,0.6)",
  };

  const headerStyle = {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: 10,
  };

  const navBtnStyle = {
    background: "none",
    border: "1px solid #2a2a2e",
    borderRadius: 5,
    color: "#888",
    fontSize: 13,
    padding: "2px 8px",
    cursor: "pointer",
    fontFamily: "'DM Mono', monospace",
    lineHeight: 1.4,
  };

  const monthLabelStyle = {
    fontFamily: "'Fraunces', Georgia, serif",
    fontSize: 13,
    fontWeight: 600,
    color: "#f0ede6",
    letterSpacing: "-0.01em",
  };

  const gridStyle = {
    display: "grid",
    gridTemplateColumns: "repeat(7, 1fr)",
    gap: 2,
  };

  return (
    <div ref={ref} style={{ position: "relative" }}>
      <button type="button" style={triggerStyle} onClick={() => setOpen(o => !o)}>
        <span>{displayDate}</span>
        <span style={{ fontSize: 10, color: "#444" }}>▼</span>
      </button>

      {open && (
        <div style={popupStyle}>
          <div style={headerStyle}>
            <button type="button" style={navBtnStyle} onClick={prevMonth}>‹</button>
            <span style={monthLabelStyle}>
              {MONTHS[viewMonth.getMonth()].slice(0, 3)} {viewMonth.getFullYear()}
            </span>
            <button type="button" style={navBtnStyle} onClick={nextMonth}>›</button>
          </div>

          <div style={gridStyle}>
            {DAYS_SHORT.map(d => (
              <div key={d} style={{ fontSize: 8, color: "#333", textAlign: "center", padding: "3px 0", letterSpacing: "0.08em" }}>
                {d}
              </div>
            ))}
            {days.map(({ date, current }, i) => {
              const str = toStr(date);
              const isSelected = str === value;
              const isToday = str === todayStr;
              return (
                <button
                  key={i}
                  type="button"
                  onClick={() => current && select(str)}
                  style={{
                    background: isSelected ? "#1D9E75" : isToday ? "#111122" : "none",
                    border: isSelected ? "none" : isToday ? "1px solid #222244" : "none",
                    borderRadius: 5,
                    color: isSelected ? "#fff" : isToday ? "#7777cc" : current ? "#b8b5ae" : "#2a2a2e",
                    fontSize: 11,
                    padding: "4px 0",
                    cursor: current ? "pointer" : "default",
                    fontFamily: "'DM Mono', monospace",
                    width: "100%",
                    textAlign: "center",
                  }}
                >
                  {date.getDate()}
                </button>
              );
            })}
          </div>

          <div style={{ marginTop: 10, paddingTop: 10, borderTop: "1px solid #1e1e22", display: "flex", justifyContent: "center" }}>
            <button
              type="button"
              onClick={() => select(todayStr)}
              style={{ ...navBtnStyle, fontSize: 10, letterSpacing: "0.06em", color: "#555" }}
            >
              Today
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
